import React, { useState, useEffect, useMemo } from 'react';
import { PreguntaCancion, Dificultad, Modalidad, CheckAnswerResponse } from '../types';
import Autocomplete from './Autocomplete';
import AudioPlayer from './AudioPlayer';
import { getImageUrl } from '../config';

interface Props {
  pregunta: PreguntaCancion;
  onResponder: (respuestaPrincipal: string, respuestaSecundaria?: string) => void;
  respondida: boolean;
  fueCorrecta: boolean | null;
  opciones: string[]; // títulos de juegos
  dificultad: Dificultad;
  desglose?: CheckAnswerResponse['desglose'];
  respuestaJugador?: string;
  onContinuar?: () => void;
  esUltima?: boolean;
  modalidad?: Modalidad;
  puntosGanados?: number;
}

const Level4Cancion: React.FC<Props> = ({
  pregunta,
  onResponder,
  respondida,
  fueCorrecta,
  opciones,
  dificultad,
  desglose,
  respuestaJugador,
  onContinuar,
  esUltima,
  modalidad,
  puntosGanados = 0,
}) => {
  const normalizeOption = (text: string) => text
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

  const [respuesta, setRespuesta] = useState('');
  const letras = ['A', 'B', 'C', 'D'];

  const mezclarOpciones = (arr: string[]) => {
    const copia = [...arr];
    for (let i = copia.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copia[i], copia[j]] = [copia[j], copia[i]];
    }
    return copia;
  };

  const opcionesNormal = useMemo(() => {
    if (dificultad !== 'normal') return [];
    const tituloCorrecto = pregunta.juegos[0]?.titulo || '';
    if (!tituloCorrecto) return [];
    const titulosValidos = new Set(pregunta.juegos.map(j => j.titulo));
    const incorrectas = opciones.filter(o => !titulosValidos.has(o));
    const mezcladas = mezclarOpciones(incorrectas).slice(0, 3);
    return mezclarOpciones([...mezcladas, tituloCorrecto]);
  }, [dificultad, opciones, pregunta.id, pregunta.juegos]);

  const puedeConfirmar = opciones.some(o => normalizeOption(o) === normalizeOption(respuesta));

  const handleConfirmar = () => {
    if (puedeConfirmar) {
      onResponder(respuesta.trim());
    }
  };

  useEffect(() => {
    setRespuesta('');
  }, [pregunta.id]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.repeat) return;

      if (respondida) {
        if (onContinuar) {
          e.preventDefault();
          onContinuar();
        }
        return;
      }

      if (puedeConfirmar) {
        e.preventDefault();
        handleConfirmar();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [respondida, onContinuar, puedeConfirmar, respuesta, onResponder]);

  // Obtener la primera portada disponible entre los juegos de la canción
  const portadaPath = useMemo(() => {
    const juegoConPortada = pregunta.juegos.find(j => j.portada);
    return juegoConPortada?.portada || null;
  }, [pregunta.juegos]);

  const startTime = parseInt(pregunta.segundoInicio || '0') || 0;

  const getRespuestaFieldColor = () => {
    if (!respondida) return 'bg-white border-2 border-emerald-200 text-slate-700';
    if (dificultad !== 'dificil' || !desglose) {
      return fueCorrecta
        ? 'bg-green-100 border-2 border-green-500 text-green-800'
        : 'bg-red-200 border-2 border-red-700 text-red-900';
    }

    if (desglose.esIndependiente) {
      return desglose.tituloCorrect
        ? 'bg-green-100 border-2 border-green-500 text-green-800'
        : 'bg-red-200 border-2 border-red-700 text-red-900';
    }

    if (desglose.tituloCorrect) return 'bg-green-100 border-2 border-green-500 text-green-800';
    if (desglose.sagaCorrect) return 'bg-amber-100 border-2 border-amber-400 text-amber-900';
    return 'bg-red-200 border-2 border-red-700 text-red-900';
  };

  const getEstiloOpcion = (opcion: string) => {
    const isSelected = respuesta === opcion;
    const titulosCorrectos = new Set(pregunta.juegos.map(j => j.titulo));
    const isCorrect = titulosCorrectos.has(opcion);

    if (respondida) {
      if (isSelected && fueCorrecta) {
        return {
          wrapper: 'border-green-500 bg-green-100 shadow-lg scale-[1.01]',
          text: 'opcion-texto-correcto',
          circle: 'border-green-500 bg-green-500 text-white',
        };
      }
      if (isCorrect) {
        return {
          wrapper: 'border-green-500 bg-green-100 shadow-lg scale-[1.01]',
          text: 'opcion-texto-correcto',
          circle: 'border-green-500 bg-green-500 text-white',
        };
      }
      if (isSelected && !fueCorrecta) {
        return {
          wrapper: 'border-red-500 bg-red-100 shadow-lg scale-[1.01]',
          text: 'opcion-texto-incorrecto',
          circle: 'border-red-500 bg-red-500 text-white',
        };
      }
      return {
        wrapper: 'border-gray-400 bg-gray-100',
        text: 'opcion-texto-resuelto',
        circle: 'border-gray-400 bg-gray-400 text-white',
      };
    }

    if (isSelected) {
      return {
        wrapper: 'border-yellow-500 bg-yellow-100 shadow-lg scale-[1.01]',
        text: 'opcion-texto-seleccionado',
        circle: 'border-yellow-500 bg-yellow-500 text-white',
      };
    }

    return {
      wrapper: 'border-slate-300 bg-white hover:border-emerald-300',
      text: 'opcion-texto-normal',
      circle: 'border-slate-400 text-slate-500 bg-white',
    };
  };

  return (
    <div className="animate-fade-in">
      {/* Reproductor de Audio o Portada del juego */}
      <div className="mb-6">
        {respondida && portadaPath ? (
          <div
            className="game-image-container animate-fade-in-scale mx-auto"
            style={{ backgroundColor: 'transparent' }}
          >
            {/* Imagen de fondo borrosa */}
            <img
              src={getImageUrl(portadaPath)}
              alt="Fondo borroso"
              className="absolute inset-0 w-full h-full object-cover animate-fade-in-blurred"
              style={{ filter: 'blur(20px)', transform: 'scale(1.1)' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            {/* Imagen principal nítida */}
            <img
              src={getImageUrl(portadaPath)}
              alt="Portada del juego"
              className="relative z-10 max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-fade-in-sharp"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        ) : (
          <AudioPlayer
            youtubeUrl={pregunta.link}
            startTime={startTime}
            cancionNumber={1}
            totalCanciones={1}
            isVisible={true}
            hideVideo={true}
            isResolved={respondida}
            defaultVolume={50}
            showParticipantControls={true}
          />
        )}
      </div>

      {/* Nombre de la canción al resolver */}
      {respondida && (
        <div className="mb-6 bg-white/5 border border-white/20 rounded-lg p-4 text-center mx-auto" style={{ width: 500, maxWidth: '100%' }}>
          <div className="text-lg font-gaming text-gaming-cyan">
            "{pregunta.respuesta}"
          </div>
          {pregunta.juegos.length > 0 && (
            <p className="font-main text-white/50 text-sm mt-1">
              {pregunta.juegos.length > 1
                ? `Aparece en varios títulos de la saga ${pregunta.juegos[0].saga}`
                : `Aparece en: ${pregunta.juegos[0].titulo}`}
            </p>
          )}
        </div>
      )}

      {dificultad === 'normal' ? (
        <div className="w-full mx-auto space-y-3 bg-transparent p-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {opcionesNormal.map((opcion, i) => {
              const estilo = getEstiloOpcion(opcion);
              return (
                <button
                  key={`${pregunta.id}-${i}-${opcion}`}
                  type="button"
                  onClick={() => !respondida && setRespuesta(opcion)}
                  disabled={respondida}
                  className={`opcion-enhanced p-4 rounded-lg border-2 transition-all duration-200 min-h-[84px] flex items-center w-full text-left ${estilo.wrapper}`}
                >
                  <div className="flex items-center w-full">
                    <span className={`circulo-letra-enhanced w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0 ${estilo.circle}`}>
                      {letras[i]}
                    </span>
                    <span className={`text-base md:text-lg flex-1 leading-tight ${estilo.text}`}>
                      {opcion}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {!respondida && (
            <button
              onClick={handleConfirmar}
              disabled={!puedeConfirmar}
              className="btn-primary mt-2 w-full font-gaming text-xs tracking-wider py-4 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              CONFIRMAR RESPUESTA
            </button>
          )}

          {respondida && onContinuar && (
            <button
              onClick={onContinuar}
              className="btn-primary w-full font-gaming text-xs tracking-wider py-4"
            >
              {puntosGanados === 0 && modalidad === 'nohit'
                ? 'VER RESULTADOS'
                : esUltima
                  ? 'VER RESULTADOS'
                  : 'SIGUIENTE CANCIÓN →'
              }
            </button>
          )}
        </div>
      ) : !respondida ? (
        <div className="max-w-md mx-auto space-y-3 panel-respuesta-enhanced p-4">
          <Autocomplete
            opciones={opciones}
            valor={respuesta}
            onChange={setRespuesta}
            placeholder="Escribe el nombre del juego..."
          />
          <button
            onClick={handleConfirmar}
            disabled={!puedeConfirmar}
            className="btn-primary mt-2 w-full font-gaming text-xs tracking-wider py-4 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            CONFIRMAR RESPUESTA
          </button>
        </div>
      ) : (
        <div className="w-full max-w-[500px] mx-auto space-y-4 text-center">
          <div className={`p-4 rounded-xl font-main text-base font-bold ${getRespuestaFieldColor()}`}>
            {respuestaJugador || '(vacío)'}
          </div>

          {onContinuar && (
            <button onClick={onContinuar} className="btn-primary w-full font-gaming text-xs tracking-wider py-4">
              {puntosGanados === 0 && modalidad === 'nohit'
                ? 'VER RESULTADOS'
                : esUltima
                  ? 'VER RESULTADOS'
                  : 'SIGUIENTE CANCIÓN →'
              }
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Level4Cancion;
